/**
 * Toast Notification System — Soorgaa Design System
 * 
 * Accessible, token-driven toast provider and hook without external dependencies.
 * Positioned above BottomNav on mobile (bottom-center) and bottom-right on desktop.
 * 
 * Usage:
 * const { toast } = useToast();
 * toast.success("Question answers saved");
 * toast.error("Could not reach the server. Please try again.");
 * toast.info("Exam discarded", { action: { label: "Undo", onClick: restoreExam } });
 */
import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from "lucide-react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  id?: string;
  action?: ToastAction;
  duration?: number;
}

export interface ToastItem {
  id: string;
  type: ToastType;
  message: React.ReactNode;
  action?: ToastAction;
  duration: number;
}

interface ToastContextValue {
  toasts: ToastItem[];
  showToast: (type: ToastType, message: React.ReactNode, options?: ToastOptions) => string;
  dismissToast: (id: string) => void;
  toast: {
    success: (message: React.ReactNode, options?: ToastOptions) => string;
    error: (message: React.ReactNode, options?: ToastOptions) => string;
    warning: (message: React.ReactNode, options?: ToastOptions) => string;
    info: (message: React.ReactNode, options?: ToastOptions) => string;
    dismiss: (id: string) => void;
  };
}

const ToastContext = createContext<ToastContextValue | null>(null);

let globalToastHandler: ((type: ToastType, message: React.ReactNode, options?: ToastOptions) => string) | null = null;
let globalDismissHandler: ((id: string) => void) | null = null;

export const toast = {
  success: (msg: React.ReactNode, opt?: ToastOptions) => globalToastHandler?.("success", msg, opt) || "",
  error: (msg: React.ReactNode, opt?: ToastOptions) => globalToastHandler?.("error", msg, opt) || "",
  warning: (msg: React.ReactNode, opt?: ToastOptions) => globalToastHandler?.("warning", msg, opt) || "",
  info: (msg: React.ReactNode, opt?: ToastOptions) => globalToastHandler?.("info", msg, opt) || "",
  dismiss: (id: string) => globalDismissHandler?.(id),
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismissToast = useCallback((id: string) => {
    const existingTimeout = timeoutsRef.current.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      timeoutsRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: React.ReactNode, options?: ToastOptions) => {
      const id = options?.id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const defaultDuration = type === "error" ? 6000 : 4000;
      const duration = options?.duration ?? defaultDuration;

      const newItem: ToastItem = {
        id,
        type,
        message,
        action: options?.action,
        duration,
      };

      setToasts((prev) => {
        const filtered = prev.filter((t) => t.id !== id);
        // Max 3 visible toasts
        return [...filtered.slice(-2), newItem];
      });

      if (duration > 0) {
        const timeout = setTimeout(() => {
          dismissToast(id);
        }, duration);
        timeoutsRef.current.set(id, timeout);
      }

      return id;
    },
    [dismissToast]
  );

  globalToastHandler = showToast;
  globalDismissHandler = dismissToast;

  const value: ToastContextValue = {
    toasts,
    showToast,
    dismissToast,
    toast: {
      success: (msg, opt) => showToast("success", msg, opt),
      error: (msg, opt) => showToast("error", msg, opt),
      warning: (msg, opt) => showToast("warning", msg, opt),
      info: (msg, opt) => showToast("info", msg, opt),
      dismiss: dismissToast,
    },
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Toast viewport container */}
      <div
        className="fixed z-50 pointer-events-none flex flex-col gap-2 p-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] left-0 right-0 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md items-center sm:items-end"
        aria-label="Notifications"
      >
        {toasts.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={() => dismissToast(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const isError = item.type === "error";

  const icons = {
    success: <CheckCircle2 size={18} className="text-success shrink-0" />,
    error: <AlertCircle size={18} className="text-destructive shrink-0" />,
    warning: <AlertTriangle size={18} className="text-warning shrink-0" />,
    info: <Info size={18} className="text-info shrink-0" />,
  };

  const borderClasses = {
    success: "border-success/30",
    error: "border-destructive/30",
    warning: "border-warning/30",
    info: "border-info/30",
  };

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={`pointer-events-auto flex items-center justify-between gap-3 px-4 py-3 bg-card border ${borderClasses[item.type]} rounded-lg shadow-lg w-full max-w-sm sm:max-w-md animate-in slide-in-from-bottom-2 duration-150 text-foreground`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {icons[item.type]}
        <div className="text-xs sm:text-sm font-medium leading-snug truncate">{item.message}</div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {item.action && (
          <button
            type="button"
            onClick={() => {
              item.action?.onClick();
              onDismiss();
            }}
            className="text-xs font-bold text-primary hover:underline px-2 py-1 rounded-xs uppercase tracking-wide cursor-pointer"
          >
            {item.action.label}
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="p-1 text-muted-foreground hover:text-foreground rounded-xs transition-colors cursor-pointer"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      toast,
      showToast: (type: ToastType, msg: React.ReactNode, opt?: ToastOptions) =>
        globalToastHandler?.(type, msg, opt) || "",
      dismissToast: (id: string) => globalDismissHandler?.(id),
      toasts: [],
    };
  }
  return ctx;
}
